
/**
 * Get authentication token from session storage
 */
import {cheeriosV2API, remoteAPI} from '@/utils/axios.ts'
import { UserSchema, SiteDeviceSchema } from '@/schemas'

// UUID generator that works in non-secure contexts (mobile browsers without HTTPS)
export function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID()
    }
    // Fallback for non-secure contexts
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0
        const v = c === 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
    })
}

export function getAuthToken() {
    return sessionStorage.getItem('auth_token')
}

export function clearSessionStorage() {
    sessionStorage.removeItem('auth_token')
    sessionStorage.removeItem('current_user')
    Object.keys(sessionStorage)
        .filter(key => key.startsWith('api_'))
        .forEach(key => sessionStorage.removeItem(key))
}

/**
 * Create standard headers for API requests
 */
export function createHeaders() {
    return {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
    }
}

export async function doCommand(deviceId, command, params = {}, siteId = null, requestId = null) {
    const token = getAuthToken()
    if (!token) throw new Error('No auth token')

    const isSite = deviceId === 'all'
    const url = isSite ? '/api/control/site' : `/api/control/tv/${deviceId}`

    const {app, ...p} = params

    const body = isSite
        ? {site_id: siteId, command, app: app || 'system', params: p}
        : {command, app, params: p, request_id: requestId}

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    })

    const data = await response.json()

    // Device is busy — not a real failure, caller should silently ignore
    if (response.status === 409) {
        return {busy: true, deviceName: data.device_name}
    }

    if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`)
    }

    return {busy: false, request_id: data.request_id}
}

export async function doLogin(email, password) {
    try {
        const tokenResponse = await cheeriosV2API.post(
            '/auth/token',
            {
                username: email,
                password: password
            },
            {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            }
        )

        const accessToken = tokenResponse.data.access_token

        sessionStorage.setItem('auth_token', accessToken);

        const currentUserResponse = await remoteAPI.get('/sites/me')

        const parseCurrentUser = UserSchema.parse(currentUserResponse.data)

        sessionStorage.setItem('current_user', JSON.stringify(parseCurrentUser));

        return {ok: true, data: parseCurrentUser};
    } catch (e) {
        console.error('[API] Login error:', e);
        
        // Check if it's an axios error with response
        if (e.response) {
            if (e.response.status === 401) {
                return {ok: false, error: 'Incorrect email or password'};
            }
            if (e.response.status >= 500) {
                return {ok: false, error: 'We hit a temporary issue. Refresh and try again.'};
            }
            return {ok: false, error: e.response.data?.message || `Server error: ${e.response.status}`};
        }
        
        // Network or other errors
        return {ok: false, error: 'We hit a temporary issue. Refresh and try again.'};
    }
}

/**
 * Get list of games available for the current site.
 * @param {Object} app  app.key
 * @param siteId
 * @returns {Promise<Object>} Object containing array of games, and available status
 */
export async function fetchGamesForSite(app, siteId) {
    try {
        const response = await fetch(`${app.app_url}/api/getgamesforsite/?siteid=${siteId}`)

        const data = await response.json()

        if (!response.ok) {
            console.warn(`[API] Games unavailable for app ${app.label}: ${data.message}`)
            return {games: [], unavailable: true, message: data.message}
        }

        return {games: data, unavailable: false}
    } catch (e) {
        console.log(`[API] No games returned for app ${app.label}: ${e}`)
        // return []
        return {games: [], unavailable: true, message: ''}
    }
}

/**
 * Validate a manually-entered game ID.
 * @param {Object} app  app.key
 * @param {string} id game ID to validate
 * @returns {Promise<Object>} Object containing valid boolean and availability status
 */
export async function validateGameId(app, id) {
    try {
        const response = await fetch(`${app.app_url}/api/validateid/?id=${id.toUpperCase()}`)

        const data = await response.json()

        if (!response.ok) {
            console.warn(`[API] Validation unavailable for app ${app.label}: ${data?.message}`)
            return {valid: false, unavailable: true, message: data?.message}
        }

        return {valid: (typeof data === 'boolean' ? data : data?.valid) === true, unavailable: false}
    } catch (e) {
        return {valid: false, unavailable: true, message: 'Request failed'}
    }
}

/**
 * Fetch groups for site
 * @param {string} siteId  logged in user's site ID
 * @returns {Promise<Object>}
 */
export async function fetchGroups(siteId) {
    try {
        const token = getAuthToken()

        const response = await fetch(`/api/groups?site_id=${siteId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        })

        if (!response.ok) throw new Error(`${response.status}`)

        return await response.json()
    } catch (e) {
        console.log(`[API] Unable to fetch groups for ${siteId}: ${e}`)
    }
}

/**
 * Fetch devices for site
 * @param {string} siteId  logged in user's site ID
 * @returns {Promise<Object>}
 */
export async function fetchDevices(siteId) {
    try {
        console.log('[API] Fetching devices from /sites/me/devices')
        const deviceResponse = await remoteAPI.get('/sites/me/devices')
        console.log('[API] Raw device response:', deviceResponse.data)
        
        const parsedDeviceData = SiteDeviceSchema.array().parse(deviceResponse.data)
        console.log('[API] Parsed devices:', parsedDeviceData)

        return parsedDeviceData
    } catch (e) {
        console.error(`[API] Unable to fetch devices for ${siteId}:`, e)
        if (e.errors) {
            console.error('[API] Zod validation errors:', e.errors)
        }
        return []
    }
}

/**
 * Request device deletion
 * @param {string} deviceId - ID of device to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function requestDeviceDeletion(deviceId) {
    try {
        const response = await fetch(`/api/tvs/${deviceId}/deletion-request`, {
            method: 'POST',
            headers: createHeaders(),
            body: JSON.stringify({
                reason: 'User requested deletion via UI'
            })
        })

        if (response.ok) {
            return {success: true}
        } else {
            const error = await response.json()
            return {success: false, error: error.message || 'Failed to delete device'}
        }
    } catch (error) {
        console.error('[API] Device deletion error:', error)
        return {success: false, error: 'Network error. Please check your connection.'}
    }
}

/**
 * Request group deletion
 * @param {string} groupId - ID of group to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function requestGroupDeletion(groupId) {
    try {
        const response = await fetch(`/api/groups/${groupId}/deletion-request`, {
            method: 'POST',
            headers: createHeaders(),
            body: JSON.stringify({
                reason: 'User requested deletion via UI'
            })
        })

        if (response.ok) {
            return {success: true}
        } else {
            const error = await response.json()
            return {success: false, error: error.message || 'Failed to delete group'}
        }
    } catch (error) {
        console.error('[API] Group deletion error:', error)
        return {success: false, error: 'Network error. Please check your connection.'}
    }
}

/**
 * Update device
 * @param {string} deviceId - ID of device to update
 * @param {Object} data - Update data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function updateDevice(deviceId, data) {
    try {
        const response = await fetch(`/api/tvs/${deviceId}`, {
            method: 'PATCH',
            headers: createHeaders(),
            body: JSON.stringify(data)
        })

        if (response.ok) {
            const result = await response.json()
            return {success: true, data: result.tv}
        } else {
            const error = await response.json()
            return {success: false, error: error.message || 'Failed to update device'}
        }
    } catch (error) {
        console.error('[API] Device update error:', error)
        return {success: false, error: 'Network error. Please check your connection.'}
    }
}

/**
 * Update group
 * @param {string} groupId - ID of group to update
 * @param {Object} data - Update data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function updateGroup(groupId, data) {
    try {
        const response = await fetch(`/api/groups/${groupId}`, {
            method: 'PATCH',
            headers: createHeaders(),
            body: JSON.stringify(data)
        })

        if (response.ok) {
            const result = await response.json()
            return {success: true, data: result.group}
        } else {
            const error = await response.json()
            return {success: false, error: error.message || 'Failed to update group'}
        }
    } catch (error) {
        console.error('[API] Group update error:', error)
        return {success: false, error: 'Network error. Please check your connection.'}
    }
}